import {getHandlerByName} from "./handlers";
import Processing from "./Processing";
import Creative from "./Creative";
import {throttle} from "../utils/etc";
import {extraKeys} from "./etc";
import { createCreativeByConfig } from "./config";

const localStoragePrefix = "hotlooks:";
const localStorageDefaultKey = "processing";

export class ProcessingManager {

  _listeners = [];
  _listeners2 = [];
  _processing = null;
  _handlers = [];

  /** @return {Processing} */
  get processing() {
    return this._processing;
  }

  /** @param {Processing} processing */
  start = (processing) => {
    this.stop();

    this._processing = processing;

    if (!processing.hasExtra(Processing.EXTRA_STARTED_AT)) {
      processing.setExtra(Processing.EXTRA_STARTED_AT, Date.now());
    }

    this.commit(processing.toObject());
    this.update();
  };

  retryCreative = (creative) => {
    creative.markAsPending();

    if (creative.getExtra(extraKeys.seeds)) {
      const filteredSeeds = creative.getExtra(extraKeys.seeds).filter((s, index) => index !== 0);
      creative.setExtra(extraKeys.seeds, filteredSeeds);
    }
    // creative.clearTasks();
    // creative.clearTasksConfigs();
    creative.removeExtra(Creative.EXTRA_STARTED_AT);
    creative.removeExtra(extraKeys.badResultRetries);
    creative.setExtra(extraKeys.retries, creative.getExtra(extraKeys.retries) + 1);

    const pos = this._handlers.findIndex((item) => item.id === creative.id);
    if (pos > -1) {
      this._handlers.splice(pos, 1);
    }

    this.update();
  };

  onCreativeFinish = (processing, creative) => {
    const handlerItem = this._handlers.find((item) => item.id === creative.id);
    handlerItem.isFinished = true;

    this.fireEvent("onCreativeFinished", processing, creative);

    if (creative.isProcessed) {
      this.fireEvent("onCreativeProcessed", processing, creative);
    } else {
      this.fireEvent("onCreativeFailed", processing, creative);

      if (creative.error?.type === "bad_result") {
        const retries = creative.getExtra(extraKeys.retries, 0);
        const retriesMax = creative.getExtra(extraKeys.retriesMax, 100);
        const badResultRetries = creative.getExtra(extraKeys.badResultRetries, 0);
        const badResultRetriesMax = creative.getExtra(extraKeys.badResultRetries, 3);

        if (badResultRetries < badResultRetriesMax && retries < retriesMax) {
          const filteredSeeds = creative.getExtra(extraKeys.seeds).slice(1);
          if (filteredSeeds.length === 0) {
            return;
          }
          
          const config = creative.getExtra(extraKeys.config);

          const nextCreative = createCreativeByConfig(processing, config, {
            gender: creative.getExtra(extraKeys.gender),
            seeds: filteredSeeds,
            contextType: creative.getExtra(extraKeys.contextType),
            stylization: creative.getExtra(extraKeys.stylization),
            body: creative.getExtra(extraKeys.body),
          });

          nextCreative.setExtra(extraKeys.isPaid, creative.getExtra(extraKeys.isPaid));
          nextCreative.setExtra(extraKeys.isPayable, creative.getExtra(extraKeys.isPayable));
          nextCreative.setExtra(extraKeys.position, creative.getExtra(extraKeys.position));
          nextCreative.setExtra(Creative.EXTRA_KEEP_PENDING, false);
          nextCreative.setAsSelected(true);

          processing.replaceCreative(creative, nextCreative);
        }
      }
    }

    this.update();
  };

  update = () => {
    if (!this.processing) {
      return;
    }

    const processing = this.processing;

    processing.creatives.forEach((creative) => {
      const activeCreativesCount = this._handlers.count((c) => !c.isFinished);
      if (activeCreativesCount >= processing.getExtra(extraKeys.concurrency, 4)) {
        return;
      }

      if (creative.getExtra(Creative.EXTRA_KEEP_PENDING, false) === true) {
        return;
      }

      if (creative.isFinished) {
        return;
      }

      const isActive = this._handlers.findIndex((item) => item.id === creative.id) > -1;
      if (isActive) {
        return;
      }

      const handler = getHandlerByName(creative.handler);
      if (handler === null) {
        throw new Error("Unrecognized handler name: " + creative.handler);
      }

      if (!creative.hasExtra(Creative.EXTRA_STARTED_AT)) {
        creative.setExtra(Creative.EXTRA_STARTED_AT, Date.now());
      }

      this.fireEvent("onCreativeStarted", processing, creative);

      this._handlers.push({
        id: creative.id,
        isFinished: false,
        handler: handler(processing, creative)
          .then((creative) => this.onCreativeFinish(processing, creative))
          .catch((creative) => this.onCreativeFinish(processing, creative)),
      });
    });

    this.tick();
  };

  /** @return {Processing} */
  restore = (cacheKey) => {
    const key = localStoragePrefix + (cacheKey || localStorageDefaultKey);
    const storedValue = window.localStorage.getItem(key);

    if (storedValue != null) {
      const processing = new Processing();
      processing.fromObject(JSON.parse(storedValue));

      return processing;
    }

    return null;
  };

  stop = () => {
    this._processing = null;
  };

  clear = (cacheKey) => {
    const key = localStoragePrefix + (cacheKey || localStorageDefaultKey);
    window.localStorage.removeItem(key);

    this.stop();
  };

  tickImmediately = () => {
    if (!this.processing) {
      return;
    }

    this.commit(this.processing.toObject());

    this.fireEvent("onUpdate", this.processing);
    this._listeners.forEach((listener) => {
      listener.call(null, this.processing);
    });
  };

  tick = throttle(200, this.tickImmediately);

  commit = (object) => {
    const key = localStoragePrefix + (object.cacheKey || localStorageDefaultKey);
    window.localStorage.setItem(key, JSON.stringify(object));
  };

  commitProcessing = () => {
    this.processing && this.commit(this.processing.toObject());
  };

  fireEvent = (event, ...data) => {
    this._listeners2.forEach((listener) => {
      listener[event].call(null, ...data);
    });
  };

  addListener = (listener) => {
    const _listener = Object.assign({
      onUpdate: () => {},
      onCreativeStarted: () => {},
      onCreativeProcessed: () => {},
      onCreativeFailed: () => {},
      onCreativeFinished: () => {},
    }, listener);

    this._listeners2.push(_listener);

    return _listener;
  };

  removeListener = (listener) => {
    const pos = this._listeners2.indexOf(listener);
    if (pos > -1) {
      this._listeners2.splice(pos, 1);
    }
  };

  addOnProcessingChangeHandler = (listener) => {
    this._listeners.push(listener);
  };

  removeOnProcessingChangeHandler = (listener) => {
    const pos = this._listeners.indexOf(listener);
    if (pos >= 0) {
      this._listeners.splice(pos, 1);
    }
  };
}

export default new ProcessingManager();
